import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, PackageCheck } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import RateCalculator from "../../components/RateCalculator";
import AgentBooking from "./AgentBooking";
import { useAuth } from "../../context/AuthContext";
import { inputCls } from "../Login";
import BoxFields from "../../components/BoxFields";

const F = ({ label, children, testid }) => (
  <div>
    <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</label>
    {children}
  </div>
);

function AddressBlock({ title, value, onChange, prefix, international }) {
  const set = (k) => (e) => onChange({ ...value, [k]: e.target.value });
  const fillFromPincode = async () => {
    if (!/^\d{6}$/.test(value.pincode || "")) return;
    try {
      const r = await api.get(`/public/pincode/${value.pincode}`);
      if (r.data.city || r.data.state) {
        onChange({ ...value, city: r.data.city || value.city, state: r.data.state || value.state });
        toast.success(`Detected: ${r.data.city}, ${r.data.state}`);
      }
    } catch { /* pincode lookup is best-effort */ }
  };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6">
      <h3 className="mb-4 font-heading text-lg font-bold text-navy">{title}</h3>
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2 sm:col-span-1"><F label="Name"><input required data-testid={`${prefix}-name`} className={inputCls} value={value.name || ""} onChange={set("name")} /></F></div>
        <div className="col-span-2 sm:col-span-1"><F label="Phone"><input required data-testid={`${prefix}-phone`} className={inputCls} value={value.phone || ""} onChange={set("phone")} /></F></div>
        <div className="col-span-2"><F label="Address"><input required data-testid={`${prefix}-address`} className={inputCls} value={value.address || ""} onChange={set("address")} /></F></div>
        <div><F label="City"><input required data-testid={`${prefix}-city`} className={inputCls} value={value.city || ""} onChange={set("city")} /></F></div>
        <div><F label="State"><input required data-testid={`${prefix}-state`} className={inputCls} value={value.state || ""} onChange={set("state")} /></F></div>
        <div><F label="Pincode"><input required data-testid={`${prefix}-pincode`} className={inputCls} maxLength={6} value={value.pincode || ""} onChange={set("pincode")} onBlur={fillFromPincode} placeholder="Auto-fills city & state" /></F></div>
        {international && <div><F label="Country"><input data-testid={`${prefix}-country`} className={inputCls} value={value.country || ""} onChange={set("country")} /></F></div>}
        <div><F label="Email (opt.)"><input type="email" className={inputCls} value={value.email || ""} onChange={set("email")} /></F></div>
      </div>
    </div>
  );
}

export default function BookShipment({ initialMode = "domestic", forceClassic = false }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pickup, setPickup] = useState({});
  const [delivery, setDelivery] = useState({});
  const [pkg, setPkg] = useState({ weight: "", length: "", width: "", height: "", pieces: 1, description: "" });
  const [paymentType, setPaymentType] = useState("PREPAID");
  const [orderAmount, setOrderAmount] = useState("");
  const [selectedRate, setSelectedRate] = useState(null);
  const [mode, setMode] = useState(initialMode);
  const [booking, setBooking] = useState(false);
  const idemKey = useRef(crypto.randomUUID());
  const [booked, setBooked] = useState(null);
  const [boxInfo, setBoxInfo] = useState({ package_type: "SPS", boxes: null });
  if ((user?.role === "agent" || user?.role === "pickup") && !forceClassic) return <AgentBooking isPickup={user?.role === "pickup"} />;

  const volumetric = pkg.length && pkg.width && pkg.height
    ? ((pkg.length * pkg.width * pkg.height / 5000) * (pkg.pieces || 1)).toFixed(2) : "0.00";
  const chargeable = Math.max(parseFloat(pkg.weight) || 0, parseFloat(volumetric)).toFixed(2);

  const book = async () => {
    if (!selectedRate) { toast.error("Please calculate rates and select a courier first."); return; }
    if (booking) return;
    setBooking(true);
    try {
      const res = await api.post("/shipments", {
        pickup, delivery,
        package: { ...pkg, weight: parseFloat(pkg.weight) || 0.5, pieces: parseInt(pkg.pieces) || 1 },
        boxes: boxInfo.boxes || undefined,
        payment_type: paymentType,
        order_amount: parseFloat(orderAmount) || 0,
        cod_amount: paymentType === "COD" ? parseFloat(orderAmount) || 0 : null,
        courier_id: selectedRate.courier_id,
        courier_name: selectedRate.courier_name,
        service: selectedRate.service,
        base_price: selectedRate.base_price,
        edd: selectedRate.edd,
        is_international: selectedRate.is_international || false,
        idempotency_key: idemKey.current,
      });
      idemKey.current = crypto.randomUUID();
      setBooked(res.data);
      toast.success(`Shipment booked! AWB: ${res.data.awb}`);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBooking(false);
    }
  };

  if (booked) {
    return (
      <div data-testid="booking-success" className="mx-auto max-w-lg rounded-2xl border border-emerald-200 bg-white p-10 text-center">
        <PackageCheck className="mx-auto h-14 w-14 text-emerald-500" />
        <h2 className="mt-4 font-heading text-2xl font-bold text-navy">Shipment Booked!</h2>
        <p className="mt-2 text-sm text-slate-500">Order <span className="font-mono font-semibold">{booked.order_id}</span></p>
        <p className="mt-1 text-sm text-slate-500">AWB <span className="font-mono text-lg font-bold text-saffron">{booked.awb}</span></p>
        <p className="mt-3 rounded-xl bg-slate-50 p-3 text-sm font-semibold text-navy">Charged: ₹{booked.customer_price.toLocaleString("en-IN")} ({booked.courier_name})</p>
        <div className="mt-6 flex justify-center gap-3">
          <button onClick={() => navigate(`/track/${booked.awb}`)} data-testid="booking-track-btn"
                  className="rounded-full bg-navy px-5 py-2.5 text-sm font-bold text-white hover:bg-saffron">Track</button>
          <button onClick={() => navigate("/app/shipments")} data-testid="booking-view-all-btn"
                  className="rounded-full border border-slate-300 px-5 py-2.5 text-sm font-bold text-navy hover:bg-slate-100">My Shipments</button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="book-shipment-page" className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight text-navy sm:text-3xl">Book a Shipment</h1>
        <p className="mt-1 text-sm text-slate-500">Domestic & international courier on the INDIAFAST network</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <AddressBlock title="Pickup From" value={pickup} onChange={setPickup} prefix="pickup" />
        <AddressBlock title="Deliver To" value={delivery} onChange={setDelivery} prefix="delivery" international={mode === "international"} />
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <h3 className="mb-4 font-heading text-lg font-bold text-navy">Package Details</h3>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-6">
          <F label="Weight (KG)"><input required type="number" step="0.1" min="0.1" data-testid="pkg-weight" className={inputCls} value={pkg.weight} onChange={(e) => setPkg({ ...pkg, weight: e.target.value })} /></F>
          <F label="L (cm)"><input type="number" min="0" data-testid="pkg-length" className={inputCls} value={pkg.length} onChange={(e) => setPkg({ ...pkg, length: e.target.value })} /></F>
          <F label="W (cm)"><input type="number" min="0" data-testid="pkg-width" className={inputCls} value={pkg.width} onChange={(e) => setPkg({ ...pkg, width: e.target.value })} /></F>
          <F label="H (cm)"><input type="number" min="0" data-testid="pkg-height" className={inputCls} value={pkg.height} onChange={(e) => setPkg({ ...pkg, height: e.target.value })} /></F>
          <F label="Pieces"><input type="number" min="1" data-testid="pkg-pieces" className={inputCls} value={pkg.pieces} onChange={(e) => setPkg({ ...pkg, pieces: e.target.value })} /></F>
          <F label="Order Value ₹"><input type="number" min="0" data-testid="pkg-order-amount" className={inputCls} value={orderAmount} onChange={(e) => setOrderAmount(e.target.value)} /></F>
          <div className="col-span-2 md:col-span-3">
            <F label="Contents Description"><input data-testid="pkg-description" className={inputCls} value={pkg.description} onChange={(e) => setPkg({ ...pkg, description: e.target.value })} placeholder="e.g. Cotton kurtas x2" /></F>
          </div>
          <div className="col-span-2 md:col-span-3">
            <F label="Payment Type">
              <select data-testid="pkg-payment-type" className={inputCls} value={paymentType} onChange={(e) => setPaymentType(e.target.value)}>
                <option value="PREPAID">Prepaid (wallet)</option>
                <option value="COD">COD — Cash on Delivery</option>
              </select>
            </F>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-3 text-xs">
          <span className="rounded-full bg-slate-100 px-3 py-1.5 font-semibold text-slate-600">Actual: {pkg.weight || 0} kg</span>
          <span className="rounded-full bg-slate-100 px-3 py-1.5 font-semibold text-slate-600">Volumetric: {volumetric} kg</span>
          <span className="rounded-full bg-saffron-light px-3 py-1.5 font-bold text-saffron" data-testid="chargeable-weight">Chargeable: {chargeable} kg</span>
        </div>
        <BoxFields totalWeight={pkg.weight} onChange={setBoxInfo} />
      </div>

      <RateCalculator onSelect={(r) => { setSelectedRate(r); toast.success(`${r.courier_name} selected — ₹${r.customer_price}`); }} onModeChange={setMode} initialMode={initialMode} />

      {selectedRate && (
        <div data-testid="selected-rate-summary" className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-saffron/40 bg-saffron-light p-5">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-saffron">Selected Courier</p>
            <p className="font-heading text-lg font-bold text-navy">{selectedRate.courier_name} · {selectedRate.service}</p>
            <p className="text-xs text-slate-500">{selectedRate.edd || "Standard delivery"}</p>
          </div>
          <p className="font-heading text-3xl font-extrabold text-navy">₹{selectedRate.customer_price.toLocaleString("en-IN")}</p>
          <button onClick={book} disabled={booking} data-testid="book-shipment-submit-btn"
                  className="flex items-center gap-2 rounded-full bg-saffron px-7 py-3 text-sm font-bold text-white shadow-lg shadow-saffron/30 transition-colors hover:bg-saffron-hover disabled:opacity-60">
            {booking && <Loader2 className="h-4 w-4 animate-spin" />} Confirm Booking
          </button>
        </div>
      )}
    </div>
  );
}
